import { format, isValid, parse, parseISO } from 'date-fns';
import CryptoJS from 'crypto-js';

export const getExistingKeys = (stateKeys, obj) => {
  const merged = { ...stateKeys };

  Object.keys(stateKeys).forEach((keyA) => {
    const matchingKeyB = Object.keys(obj).find((keyB) => keyB.toLowerCase() === keyA.toLowerCase());

    if (matchingKeyB) {
      merged[keyA] = obj[matchingKeyB];
    }
  });

  return merged || {};
};

export const changeCurrencyFormat = (amount) => {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount));
  return formattedAmount;
};

export const changeDateFormat = (date, options = {}) => {
  const { dateFormat = 'dd-MM-yyyy', fallback = '\u00A0', inputFormat } = options;

  // Handle different types of input
  let parsedDate;

  if (typeof date === 'string') {
    if (inputFormat) {
      parsedDate = parse(date, inputFormat, new Date()); // Parse using provided format
    } else {
      parsedDate = parseISO(date); // Default to ISO parsing
    }
  } else if (typeof date === 'number') {
    parsedDate = new Date(date); // Treat as timestamp
  } else {
    parsedDate = date; // Assume it's already a Date object
  }

  // Validate and format the date
  return isValid(parsedDate) ? format(parsedDate, dateFormat) : fallback;
};


export const encryptLocalStorageObj = (obj) => {
  const encryptedDataAID = CryptoJS.AES.encrypt(JSON.stringify(obj), process.env.REACT_APP_CRYPTOJS_SECRET_KEY).toString();
  localStorage.setItem('AID', encryptedDataAID);
};

export const decryptLocalStorageObj = () => {
  const encryptedDataFromStorage = localStorage.getItem('AID');
  let decryptedData = {};
  if (encryptedDataFromStorage) {
    const bytes = CryptoJS.AES.decrypt(encryptedDataFromStorage, process.env.REACT_APP_CRYPTOJS_SECRET_KEY);
    decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }
  return decryptedData;
};

export const generateColor = (theme) => {
  const getRandomValue = (min, max) => 
    Math.floor(Math.random() * (max - min + 1)) + min;

  let r, g, b;

  if (theme === "light") {
    // Generate light colors (RGB values closer to 255 but varied)
    r = getRandomValue(200, 255);
    g = getRandomValue(200, 255);
    b = getRandomValue(200, 255);
  } else if (theme === "dark") {
    // Generate dark colors (RGB values closer to 0 but varied)
    r = getRandomValue(0, 100);
    g = getRandomValue(0, 100);
    b = getRandomValue(0, 100);
  } else {
    throw new Error("Invalid theme. Please provide 'light' or 'dark'.");
  }

  // Add slight randomness to avoid uniform color tones
  const randomOffset = theme === "light" ? -30 : 30;
  const adjustChannel = (channel) => 
    Math.max(0, Math.min(255, channel + getRandomValue(randomOffset, -randomOffset)));

  r = adjustChannel(r);
  g = adjustChannel(g);
  b = adjustChannel(b);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};


const usedColors = new Set();

export function generateUniqueColor(type = 'light') {
  let color;

  do {
    // Generate RGB values
    const r = type === 'light' ? getRandomInt(128, 255) : getRandomInt(0, 127);
    const g = type === 'light' ? getRandomInt(128, 255) : getRandomInt(0, 127);
    const b = type === 'light' ? getRandomInt(128, 255) : getRandomInt(0, 127);

    // Convert to HEX color code
    color = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } while (usedColors.has(color)); // Ensure the color is unique

  // Add to used colors
  usedColors.add(color);

  return color;
}

// Helper function to generate a random integer between min and max (inclusive)
function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Helper function to convert a number to a 2-digit HEX string
function toHex(num) {
  return num.toString(16).padStart(2, '0');
}
